import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

// Everything closed until the server says otherwise. A hidden area flashing into the
// navigation and back out again is worse than appearing a moment late.
const CLOSED = { community: false, payments: false, messages: false };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("costock_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);
  // What a visitor who is not signed in may see. A signed-in user carries their own
  // answer on `user.features`; both come from backend/features.py.
  const [publicFeatures, setPublicFeatures] = useState(CLOSED);
  const [publicFeaturesLoading, setPublicFeaturesLoading] = useState(true);

  useEffect(() => {
    api.siteFeatures()
      .then((r) => setPublicFeatures({ ...CLOSED, ...r.data }))
      .catch(() => {})
      .finally(() => setPublicFeaturesLoading(false));
  }, []);

  // The session is an HttpOnly cookie, so there is no token to look for before
  // deciding whether to ask. The server is asked every time and "not signed in" is
  // an ordinary answer — api.me() is flagged so a 401 does not bounce a logged-out
  // visitor off a public page.
  //
  // The cached costock_user is kept, but only as a render hint so the shell does
  // not flash logged-out on load. It carries no credential and grants nothing: the
  // backend re-derives role, tier and every entitlement on each request.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Sessions that predate the cookie deploy still have a token in localStorage.
      // Trade it for cookies before asking who we are, or the first /me would 401
      // and sign the user out — which is the mass logout this migration avoids.
      await api.migrateLegacyToken();
      try {
        const res = await api.me();
        if (cancelled) return;
        setUser(res.data);
        localStorage.setItem("costock_user", JSON.stringify(res.data));
      } catch {
        if (cancelled) return;
        localStorage.removeItem("costock_user");
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // No token argument any more — the cookies were set by the response that produced
  // this user, and the client never sees them.
  const completeLogin = (u) => {
    localStorage.setItem("costock_user", JSON.stringify(u));
    setUser(u);
    return u;
  };

  const logout = async () => {
    // Actually ends the session now: the server revokes the refresh token and clears
    // the cookies. Previously the client just forgot a token that stayed valid for
    // the rest of its week.
    try { await api.logout(); } catch {}
    localStorage.removeItem("costock_user");
    setUser(null);
    window.location.href = "/login";
  };

  const patchUser = (partial) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...partial };
      localStorage.setItem("costock_user", JSON.stringify(next));
      return next;
    });
  };

  // Entitlement helpers. These mirror the server's rules for convenience only —
  // the backend re-checks every one of them, so hiding a button here is a UX
  // affordance, never the actual access control.
  const role = user?.role || "user";
  // The account's real role, independent of any admin preview. Only the preview
  // switch itself should key off this — everything else must obey `role`, or the
  // preview would stop being a faithful view of what other people see.
  const realRole = user?.real_role || role;
  const features = user ? { ...CLOSED, ...(user.features || {}) } : publicFeatures;
  const entitlements = {
    role,
    // Which product areas are open to this account. The server answers 404 for a
    // closed area whatever the UI does; this only keeps the UI from offering it.
    features,
    // Route guards wait for this rather than redirecting on the closed default.
    featuresLoading: loading || (!user && publicFeaturesLoading),
    tier: user?.tier || "free",
    isAdmin: role === "admin",
    isInvestor: role === "investor" || role === "admin",
    isPaid: (user?.tier || "free") === "paid",
    realRole,
    isRealAdmin: realRole === "admin",
    adminPreview: user?.admin_preview || null,
  };

  return (
    <AuthContext.Provider value={{ user, loading, completeLogin, logout, patchUser, ...entitlements }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
